import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Save,
    X,
    FileText,
    CheckSquare,
    Info,
    CheckCircle2,
    Clock,
    AlertCircle,
    Download,
    Mail,
    Printer,
    BarChart3,
    User,
    Calendar,
    ChevronDown,
    ChevronUp,
    Search,
    Filter,
    ArrowLeft,
    Target,
    TrendingUp,
} from "lucide-react";
import {
    getStatusAuditDetails,
    updateStatusAuditDetails,
} from "@/redux/Booking/actions";
import { useDispatch, useSelector } from "react-redux";
import { callLoader } from "@/redux/Loader/actions";
import {
    defaultData,
    generatePayload,
    mapStatusObject,
} from "./statusAuditHelper";
import moment from "moment";
import DateInputwithcal from "@/components/ui/DateInputwithcal";

import { deepClone } from "@/utility/CommonConstant";
import CelebrationButton from "@/components/ui/CelebrationButton/CelebrationButton";
import { showToast } from "@/redux/Toast/actions";
import notificationService from "@/services/notificationService";
import AIInsightCard from "@/components/shipment/AIInsightCard";
import PageHeader from "@/components/FormHeader/formPageHeader";
import useAuthUser from "@/hooks/useAuthUser";
import { resolveStatusAuditContext } from "@/utils/statusAuditNavigation";

const StatusAudit = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchQuery, setSearchQuery] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");

    const [expandedGroups, setExpandedGroups] = useState({
        booking: true,
        trucking: true,
        documentation: true,
        billing: true,
        completion: true,
    });

    const [auditData, setAuditData] = useState({});
    const [updateAuditData, setUpdateAuditData] = useState({});
    const [printData, setPrintData] = useState({});
    const location = useLocation();
    const auditContext = useMemo(
        () => resolveStatusAuditContext(location),
        [location.state, location.search],
    );
    const { MasterCompanyDetails } = useSelector((state) => state.common);
    const { isAnimate } = useSelector((state) => state.toast);
     const authUser = useAuthUser();

   const userName =  authUser?.username || "";
   const companyid = authUser?.companyId || "";
   const modifyby = authUser?.username || "";

    const groupConfig = {
        booking: {
            label: "Booking & Confirmation",
            icon: FileText,
            color: "bg-blue-500",
        },
        trucking: {
            label: "Trucking & Logistics",
            icon: Clock,
            color: "bg-amber-500",
        },
        documentation: {
            label: "Documentation",
            icon: FileText,
            color: "bg-purple-500",
        },
        billing: {
            label: "Billing & Payment",
            icon: BarChart3,
            color: "bg-emerald-500",
        },
        completion: {
            label: "Completion",
            icon: CheckCircle2,
            color: "bg-pink-500",
        },
    };

    const handleCheckboxChange = (index) => {
        const newStatuses = [...auditData.statuses];
        newStatuses[index].checked = !newStatuses[index].checked;
        if (newStatuses[index].checked && !newStatuses[index].date) {
            const now = new Date();
            newStatuses[index].date = now.toISOString().split("T")[0];
            newStatuses[index].time = now.toTimeString().slice(0, 5);
        } else {
            newStatuses[index].date = "";
            newStatuses[index].time = "";
        }
        setAuditData({ ...auditData, statuses: newStatuses });
    };

    const handleDateChange = (index, field, value) => {
        const newStatuses = [...auditData.statuses];
        newStatuses[index][field] = value;
        setAuditData({ ...auditData, statuses: newStatuses });
    };

    const completedCount = useMemo(() => {
        return (auditData?.statuses || []).filter((s) => s.checked).length;
    }, [auditData?.statuses]);
    const totalCount = useMemo(() => {
        return (auditData?.statuses || []).length;
    }, [auditData?.statuses]);
    const pendingCount = useMemo(() => {
        return totalCount - completedCount;
    }, [totalCount, completedCount]);
    const progressPercent = useMemo(() => {
        if (!totalCount) return 0;
        return Math.round((completedCount / totalCount) * 100);
    }, [completedCount, totalCount]);
    const progressPercentage = progressPercent; // Alias for consistency

    const filteredStatuses = (auditData?.statuses || []).filter((status) => {
        const matchesSearch = status.label
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        const matchesFilter =
            filterStatus === "all" ||
            (filterStatus === "completed" && status.checked) ||
            (filterStatus === "pending" && !status.checked);
        return matchesSearch && matchesFilter;
    });

    const getGroupStatuses = useCallback(
        (group) => filteredStatuses.filter((s) => s.group === group),
        [filteredStatuses],
    );

    useEffect(() => {
        if (!auditContext?.bookingId) return;
        _getStatusAuditDetails(auditContext.bookingId);
    }, [auditContext?.bookingId]);

    const _getStatusAuditDetails = async (bookingid) => {
        try {
            dispatch(callLoader(1));
            const resultData = await dispatch(getStatusAuditDetails(bookingid));


            const obj = resultData?.[0] || { ...defaultData, bookingId: bookingid };
            console.log("data if status audit:", obj);
            setUpdateAuditData(obj);
            obj.bookingEntered = true;
            obj.bookingEnteredDt = auditContext?.createdOn;

            const mapStatus = mapStatusObject(obj);

            const data = {
                reportNumber: auditContext?.FILENUMBER,
                carrierBookingNumber: auditContext?.carrierBookingNumber,
                bookingContactPerson: auditContext?.customerName,
                bookingEnteredBy: auditContext?.createdBy,
                bookingEnteredDate: moment(auditContext?.createdOn).format(
                    "D MMM YYYY [AT] hh:mm A",
                ),
                statuses: mapStatus,
            };

            setAuditData(data);
            setPrintData(deepClone(data));
        } catch (error) {
            console.error("Error fetching booking details:", error);
            // toast.error("Failed to load booking details");
            dispatch(showToast("Failed to load booking details", "error"));
        } finally {
            dispatch(callLoader(0));
        }
    };

    const _updateStatusAuditDetails = async () => {
        const payload = generatePayload(auditData?.statuses, updateAuditData, userName);
        delete payload.bookingEntered;
        delete payload.bookingEnteredDt;
        delete payload.bookingEnteredDt;
          payload.modifiedBy = modifyby;
    payload.companyId = companyid;

        console.log('payload:', payload);


        // Track which statuses changed from printData (previous state)
        const changedStatuses = [];
        if (printData?.statuses && auditData?.statuses) {
            auditData.statuses.forEach((status, index) => {
                const prevStatus = printData.statuses[index];
                if (prevStatus && status.checked !== prevStatus.checked) {
                    changedStatuses.push({
                        label: status.label,
                        checked: status.checked,
                        group: status.group,
                    });
                }
            });
        }

        try {
            dispatch(callLoader(1));
            const resultData = await dispatch(updateStatusAuditDetails(payload));
            // const resultData = "";
            dispatch(showToast(resultData, "success", true));
            setPrintData(deepClone(auditData));

            // Trigger notification for status audit changes
            if (changedStatuses.length > 0) {
                const fileNumber = auditData?.reportNumber || auditContext?.FILENUMBER;
                const bookingId = auditContext?.bookingId;

                // Send notification with all changed statuses
                notificationService.statusAuditBulkUpdate(
                    fileNumber,
                    changedStatuses,
                    bookingId,
                );
            }
        } catch (error) {
            dispatch(showToast("Failed to update booking details", "error"));
        } finally {
            dispatch(callLoader(0));
        }
    };

    return (
        <div>
 <PageHeader
                icon={<CheckSquare />}
                title="Status Audit Report"
                badge={auditData?.reportNumber}
                subtitle="Track and manage booking status checkpoints"
                actions={[
                    {
                        label: "Back",
                        icon: <ArrowLeft />,
                        onClick: () => navigate(-1),
                        variant: "ghost",
                    },
                    "|",
                    {
                        label: "Print",
                        icon: <Printer />,
                        onClick: () => {
                            localStorage.setItem(
                                "PRINT_AUDIT_DATA",
                                JSON.stringify(printData),
                            );
                            localStorage.setItem(
                                "MASTER_COMPANY_DETAILS",
                                JSON.stringify(MasterCompanyDetails),
                            );
                            window.open("/status-audit/print", "_blank");
                        },
                        variant: "tertiary",
                    },
                    {
                        label: "Cancel",
                        icon: <X />,
                        onClick: () => navigate("/booking"),
                        variant: "black",
                    },
                ]}
                onSubmit={_updateStatusAuditDetails}
                submitLabel="Save"
                submitIcon={<Save />}
            />
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 space-y-4 pb-12"
        >
            {/* Header - Blue gradient matching Booking page */}


           

            {/* Progress Overview */}
            <div
                className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3"
                style={{
                    marginTop: "calc(var(--page-header-height) + 10px)",
                }}
            >
                <AIInsightCard
                    icon={CheckCircle2}
                    title="Completed"
                    value={completedCount}
                    variant="compact"
                    accentKey="emerald"
                />

                <AIInsightCard
                    icon={Clock}
                    title="Pending"
                    value={pendingCount}
                    variant="compact"
                    accentKey="amber"
                />

                <AIInsightCard
                    icon={Target}
                    title="Total"
                    value={totalCount}
                    variant="compact"
                    accentKey="blue"
                />

                <AIInsightCard
                    icon={TrendingUp}
                    title="Progress"
                    value={`${progressPercent}%`}
                    variant="compact"
                    accentKey="red"
                />
            </div>

            {/* Main Content */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                {/* Left Column - Status Checklist */}
                <div className="xl:col-span-2 space-y-3">
                    {/* Search & Filter */}
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 p-3">
                        <div className="flex flex-col sm:flex-row gap-2">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search checkpoints..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 rounded-lg p-1">
                                {[
                                    { value: "all", label: "All" },
                                    { value: "completed", label: "Completed" },
                                    { value: "pending", label: "Pending" },
                                ].map((filter) => (
                                    <button
                                        key={filter.value}
                                        onClick={() => setFilterStatus(filter.value)}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${filterStatus === filter.value
                                            ? "bg-blue-600 text-white"
                                            : "text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                                            }`}
                                    >
                                        {filter.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Status Groups */}
                    {Object.entries(groupConfig).map(([groupKey, config]) => {
                        const groupStatuses = getGroupStatuses(groupKey);
                        const groupCompleted = groupStatuses.filter(
                            (s) => s.checked,
                        ).length;
                        const groupTotal = groupStatuses.length;

                        if (groupTotal === 0) return null;

                        return (
                            <div
                                key={groupKey}
                                className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800"
                            >
                                <button
                                    onClick={() =>
                                        setExpandedGroups((prev) => ({
                                            ...prev,
                                            [groupKey]: !prev[groupKey],
                                        }))
                                    }
                                    className={`w-full flex items-center justify-between p-4 ${config.color} transition-all hover:opacity-90 ${expandedGroups[groupKey] ? "rounded-t-xl" : "rounded-xl"}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="bg-white/20 p-2 rounded-lg">
                                            <config.icon className="w-5 h-5 text-white" />
                                        </div>
                                        <h3 className="text-base font-semibold text-white">
                                            {config.label}
                                        </h3>
                                        <span className="px-2 py-0.5 bg-white/20 text-white text-xs font-medium rounded-full">
                                            {groupCompleted}/{groupTotal}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="w-24 bg-white/20 rounded-full h-2">
                                            <div
                                                className="bg-white h-2 rounded-full transition-all"
                                                style={{
                                                    width: `${(groupCompleted / groupTotal) * 100}%`,
                                                }}
                                            />
                                        </div>
                                        {expandedGroups[groupKey] ? (
                                            <ChevronUp className="w-5 h-5 text-white/80" />
                                        ) : (
                                            <ChevronDown className="w-5 h-5 text-white/80" />
                                        )}
                                    </div>
                                </button>

                                {expandedGroups[groupKey] && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        className="p-4 space-y-2"
                                    >
                                        {groupStatuses.map((status) => {
                                            const originalIndex = (
                                                auditData?.statuses || []
                                            ).findIndex((s) => s.id === status.id);
                                            return (
                                                <div
                                                    key={status.id}
                                                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${status.checked
                                                        ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800"
                                                        : "bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 hover:border-blue-300"
                                                        }`}
                                                >
                                                    <button
                                                        onClick={() =>
                                                            handleCheckboxChange(originalIndex)
                                                        }
                                                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${status.checked
                                                            ? "bg-emerald-500 border-emerald-500"
                                                            : "border-slate-300 dark:border-slate-600 hover:border-blue-500"
                                                            }`}
                                                    >
                                                        {status.checked && (
                                                            <CheckCircle2 className="w-4 h-4 text-white" />
                                                        )}
                                                    </button>
                                                    <div className="flex-1">
                                                        <label
                                                            className={`text-sm font-medium cursor-pointer ${status.checked
                                                                ? "text-emerald-700 dark:text-emerald-400 line-through"
                                                                : "text-slate-900 dark:text-slate-100"
                                                                }`}
                                                            onClick={() =>
                                                                handleCheckboxChange(originalIndex)
                                                            }
                                                        >
                                                            {status.label}
                                                        </label>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <DateInputwithcal
                                                            label=""
                                                            name="date"
                                                            value={status.date}
                                                            onChange={(name, value) =>
                                                                handleDateChange(originalIndex, "date", value)
                                                            }
                                                            disabled={!status.checked}
                                                        />
                                                        <input
                                                            type="time"
                                                            value={status.time}
                                                            disabled={!status.checked}
                                                            onClick={(e) => e.target.showPicker?.()}
                                                            onChange={(e) =>
                                                                handleDateChange(
                                                                    originalIndex,
                                                                    "time",
                                                                    e.target.value,
                                                                )
                                                            }
                                                            className="px-2 py-1.5 text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </motion.div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Right Column - Info & Actions */}
                <div className="space-y-6">
                    {/* Booking Contact */}
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                        <div className="bg-blue-600 p-4">
                            <div className="flex items-center gap-3">
                                <div className="bg-white/20 p-2 rounded-lg">
                                    <User className="w-5 h-5 text-white" />
                                </div>
                                <h3 className="text-base font-semibold text-white">
                                    Booking Contact
                                </h3>
                            </div>
                        </div>
                        <div className="p-5">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <User className="w-8 h-8 text-blue-600" />
                                </div>
                                <h4 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {auditData.bookingContactPerson}
                                </h4>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                                    Entered on {auditData.bookingEnteredDate}
                                </p>
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    by{" "}
                                    <span className="font-semibold text-blue-600">
                                        {auditData.bookingEnteredBy}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Progress Ring */}
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
                        <div className="text-center">
                            <div className="relative w-32 h-32 mx-auto mb-4">
                                <svg className="w-full h-full transform -rotate-90">
                                    <circle
                                        cx="64"
                                        cy="64"
                                        r="56"
                                        stroke="currentColor"
                                        strokeWidth="12"
                                        fill="none"
                                        className="text-slate-200 dark:text-slate-700"
                                    />
                                    <motion.circle
                                        cx="64"
                                        cy="64"
                                        r="56"
                                        stroke="url(#gradient)"
                                        strokeWidth="12"
                                        fill="none"
                                        strokeLinecap="round"
                                        initial={{ strokeDasharray: "0 352" }}
                                        animate={{
                                            strokeDasharray: `${(progressPercent / 100) * 352} 352`,
                                        }}
                                        transition={{ duration: 1, ease: "easeOut" }}
                                    />
                                    <defs>
                                        <linearGradient
                                            id="gradient"
                                            x1="0%"
                                            y1="0%"
                                            x2="100%"
                                            y2="0%"
                                        >
                                            <stop offset="0%" stopColor="#10B981" />
                                            <stop offset="100%" stopColor="#3B82F6" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="text-center">
                                        <span className="text-3xl font-bold text-slate-900 dark:text-white">
                                            {progressPercent}%
                                        </span>
                                        <p className="text-xs text-slate-500">Complete</p>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mt-4">
                                <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl">
                                    <p className="text-2xl font-bold text-emerald-600">
                                        {completedCount}
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        Done
                                    </p>
                                </div>
                                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
                                    <p className="text-2xl font-bold text-amber-600">
                                        {totalCount - completedCount}
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        Remaining
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                        <div className="bg-purple-600 p-4">
                            <div className="flex items-center gap-3">
                                <div className="bg-white/20 p-2 rounded-lg">
                                    <BarChart3 className="w-5 h-5 text-white" />
                                </div>
                                <h3 className="text-base font-semibold text-white">
                                    Quick Actions
                                </h3>
                            </div>
                        </div>
                        <div className="p-4 space-y-2">
                            {[
                                {
                                    icon: BarChart3,
                                    label: "Generate Status Report",
                                    color: "bg-blue-600 hover:bg-blue-700",
                                },
                                {
                                    icon: Download,
                                    label: "Export to PDF",
                                    color: "bg-emerald-600 hover:bg-emerald-700",
                                },
                                {
                                    icon: Mail,
                                    label: "Email Status Update",
                                    color: "bg-purple-600 hover:bg-purple-700",
                                },
                                {
                                    icon: Printer,
                                    label: "Print Report",
                                    color: "bg-amber-600 hover:bg-amber-700",
                                },
                            ].map((action, index) => (
                                <button
                                    onClick={() => {
                                        localStorage.setItem(
                                            "PRINT_AUDIT_DATA",
                                            JSON.stringify(printData),
                                        );
                                        localStorage.setItem(
                                            "MASTER_COMPANY_DETAILS",
                                            JSON.stringify(MasterCompanyDetails),
                                        );
                                        window.open("/status-audit/print", "_blank");
                                    }}
                                    key={index}
                                    className={`w-full px-4 py-3 ${action.color} text-white rounded-xl text-sm font-medium transition-all flex items-center gap-3 shadow-sm hover:shadow-md`}
                                >
                                    <action.icon className="w-4 h-4" />
                                    {action.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Sticky Bottom Actions */}
            <div className="p-3 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 flex items-center justify-between">

                {/* Left side info */}
                <div className="text-sm text-slate-500 dark:text-slate-400">
                    <span className="font-semibold text-emerald-600">
                        {completedCount}
                    </span>{" "}
                    of <span className="font-semibold">{totalCount}</span> checkpoints completed
                </div>

                {/* Right side actions */}
                <div className="flex items-center gap-2">

                    {/* Cancel */}
                    <button
                        type="button"
                        onClick={() => navigate("/booking")}
                        className="inline-flex items-center gap-1.5 px-4 h-[38px] text-xs font-medium rounded-md text-slate-600 hover:bg-red-50 dark:text-slate-300 dark:hover:bg-red-900/20 transition-all"
                    >
                        <X className="w-4 h-4" />
                        <span className="hidden sm:inline">Cancel</span>
                    </button>

                    {/* Divider */}
                    <div className="w-px h-5 bg-slate-200 dark:bg-slate-700" />

                    {/* Print */}
                    {auditContext?.bookingId && (
                        <button
                            type="button"
                            onClick={() =>
                                window.open(
                                    `/status-audit/print/${auditContext?.bookingId}`,
                                    "_blank"
                                )
                            }
                            className="inline-flex items-center gap-1.5 px-4 h-[38px] text-xs font-medium rounded-md bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600 transition-all"
                        >
                            <Printer className="w-4 h-4" />
                            <span className="hidden sm:inline">Print</span>
                        </button>
                    )}

                    {/* Save */}
                    <CelebrationButton
                        onClick={_updateStatusAuditDetails}
                        label={"Save Changes"}
                        className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 bg-emerald-600 text-white hover:bg-emerald-700"
                        icon={<Save className="w-4 h-4" />}
                        isSuccess={isAnimate}
                    />

                </div>
            </div>
        </motion.div>
        </div>

    );
};

export default StatusAudit;
